/**
 * @class This is a small singleton class that manages the sun in the scene. It is currently not used in the ortho scene
 */

import * as THREE from 'three';
import { Camera, MathUtils, Renderer, Scene } from 'three';

import { Cesium3dTilesPointsMaterial } from './geodata/point-clouds/cesium-3d-tiles-point-cloud-renderer/cesium-3d-tiles-points-material';
import { CesiumPointsMaterial } from './geodata/point-clouds/cesium-point-cloud/points/material/cesium-point-material';

export class SunManager {
  static _instance: SunManager = new SunManager();
  static get instance(): SunManager {
    return this._instance;
  }

  private sunAmbient = new THREE.AmbientLight(0xffffff, 3);

  private directionEnabled = false;
  private sunDirection = new THREE.DirectionalLight(0xffffff, 3);
  private sunDirectionPercent = 0;

  private hemisphereLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 1);

  private constructor() {
    this.sunDirection.castShadow = false;
    this.toggleDirectionLight(true);
  }

  /**
   * @description Add the sun ambient to a scene
   * @param {THREE.Object3D} target
   */
  addAmbient(target: THREE.Object3D) {
    target.add(this.sunAmbient);
  }

  /**
   * @description Add the sun direction to a scene
   * @param {THREE.Object3D} target
   */
  addDirection(target: THREE.Object3D) {
    target.add(this.sunDirection);
    target.add(this.hemisphereLight);
  }

  /**
   * @description Remove the ambient light to a scene
   * @param {THREE.Object3D} target
   */
  removeAmbient(target: THREE.Object3D) {
    target.remove(this.sunAmbient);
  }

  /**
   * @description Remove the direction light to a scene
   * @param {THREE.Object3D} target
   */
  removeDirection(target: THREE.Object3D) {
    target.remove(this.sunDirection);
  }

  /**
   * @description Set the direction of the sun in percent 0-100.
   * @param {integer} percent (0-100)
   */
  setDirection(percent: number) {
    if (!Number.isInteger(percent)) {
      throw new Error('Sun direction value must be an integer 0-100.');
    }

    percent = MathUtils.clamp(percent, 0, 100);

    this.sunDirectionPercent = percent;

    const azimuth = THREE.MathUtils.degToRad(90 + (percent / 100) * 180);
    // azimuth sweeps from SE → SW (sun stays southern)

    const elevation = THREE.MathUtils.degToRad(35); // fixed height

    const direction = new THREE.Vector3(
      Math.cos(elevation) * Math.cos(azimuth),
      Math.cos(elevation) * Math.sin(azimuth),
      Math.sin(elevation),
    );

    direction.normalize();

    this.sunDirection.position.copy(direction);
    CesiumPointsMaterial.instance.lightDirection = direction;
    Cesium3dTilesPointsMaterial.instance.lightDirection = direction;
  }

  renderObjectWithLightSources(renderer: Renderer, obj: Scene, camera: Camera) {
    this.addAmbient(obj);
    this.addDirection(obj);

    renderer.render(obj, camera);

    this.removeAmbient(obj);
    this.removeDirection(obj);
  }

  getDirectionPercent() {
    return this.sunDirectionPercent;
  }

  /**
   * @description Get if direction light is enabled
   * @param {boolean} state
   */
  toggleDirectionLight(state: boolean) {
    this.sunAmbient.visible = !state;
    this.hemisphereLight.visible = state;
    this.sunDirection.visible = state;
    this.directionEnabled = state;
    CesiumPointsMaterial.instance.applyShading = state;
    Cesium3dTilesPointsMaterial.instance.applyShading = state;
  }

  /**
   * @description Get if direction light is enabled
   * @returns {boolean}
   */
  isDirectionEnabled() {
    return this.directionEnabled;
  }

  cloneLights(): THREE.Light[] {
    if (this.directionEnabled) {
      return [this.sunDirection.clone(), this.hemisphereLight.clone()];
    }
    return [this.sunAmbient.clone()];
  }
}
